<template>
    <div class="bg-white">
        <div class="card"> 
            
            <div class="grid grid-cols-1 bg-white gap-0 min-w-full mt-10 mb-5" v-if="showBanner === true && Number(categoryBanner?.home_position) === 1">
                <div class="flex flex-col gap-3">
                    
                    <div class="relative overflow-auto w-full h-[300px]">
                        <div class="font-extrabold text-slate-800 text-[20px] text-center mb-5">
                            <img :src="'storage/'+ categoryBanner.image_name" class="w-full" @error="(e) => showBanner = false"/>
                        </div>
                        
                    </div>
                </div> 
            </div>
            <div class="font-extrabold text-slate-800 ml-10 text-lg md:text-lg lg:text-lg sm:text-lg  text-center mb-5">
                <Link :href="route('category.product', {category: props.category, category_name: slugify(props.categoryName.toLowerCase())})">{{ props.categoryName }}</Link>
            </div>

            
            <Carousel
                :key="isMobile ? 'mobile' : 'desktop'"
                :value="categoryProduct"
                :numVisible="isMobile ? 2 : 4"
                :numScroll="isMobile ? 2 : 1"
                :responsiveOptions="responsiveOptions"
                :autoplayInterval="3000"
                :circular="true"
                >
                <template #item="slotProps">
                    <div class="border-surface-200 dark:border-surface-700 rounded ml-0 p-0 shadow-md mb-2 relative h-[23rem]">
                        <div class="mb-4">
                            <div class="relative mx-auto h-[13rem]">
                                <Link :href="route('product.product-view', {product_name: slugify(slotProps.data.product_name), id: slotProps.data.id})"><img :src="'/storage/' + slotProps.data.product_default_image" :alt="slotProps.data.name" @error="(e) => e.target.src = noImage" class="w-52 rounded h-52 mx-auto items-center"  /></Link>
                                <Tag :value="slotProps.data.is_stock_out === 0 ? 'INSTOCK' : 'OUTOFSTOCK'" :severity="getSeverity(slotProps.data.is_stock_out)" class="absolute" style="left:5px; top: 5px"/>
                            </div>
                        </div>
                        <div class="p-4">
                            <div class="mb-4 font-medium text-left"><Link :href="route('product.product-view', {product_name: slugify(slotProps.data.product_name), id: slotProps.data.id})">{{ slotProps.data.product_name }}</Link></div>
                            <div class="flex justify-between items-center">
                                <div class="mt-3 font-semibold text-center md:text-lg lg:text-lg sm:text-lg text-md">{{ slotProps.data.sale_price }} {{ props.appSettings?.default_currency }}</div>
                                
                                <span class="flex flex-row gap-2 mt-3 items-center">
                                    <Button icon="pi pi-heart" severity="secondary" class="bg-green-700 py-1 px-10 text-white" outlined @click.prevent="($event) => makeWishList(slotProps.data)" v-if="localStorageWishListItems?.findIndex((wishList: EcommerceProductModel) => wishList.id === slotProps.data.id) !== -1" :style="{backgroundColor: 'red', color: '#fff'}"/>
                                    <Button icon="pi pi-heart" severity="secondary" class="bg-green-700 py-1 px-10 text-white" outlined @click.prevent="($event) => makeWishList(slotProps.data)" v-else/>
                                    <Button type="button" icon="pi pi-shopping-cart" class="ml-2 bg-green-700 py-1 px-10 text-white" @click.prevent="getShopingCartItems(slotProps.data)" v-if="localStorageCartItems?.findIndex((cartItem: EcommerceProductModel) => cartItem.id === slotProps.data.id) !== -1" :style="{backgroundColor: '#22C55E', color: '#fff'}"/>
                                    <Button type="button" icon="pi pi-shopping-cart" class="ml-2 bg-green-700 py-1 px-10 text-white" @click.prevent="getShopingCartItems(slotProps.data)" v-else/>
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
            </Carousel>

            
        </div>
    </div>
</template>

<script setup lang="ts">
import VideoBanner from './VideoBanner.vue';
import { ProductService } from '@/Services/ProductService';  
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import EcommerceProductModel from '@/Interfaces/Product/EcommerceProductModel';
import ProductModel from '@/Interfaces/Product/ProductModel';
import CategoryModel from '@/Interfaces/Category/CategoryModel';
import { useShoppingCartComposable } from '@/Composables/ShoppingCartComposable/useShoppingCartComposable';
import { isValidJSON } from '@/Helpers/Helpers';
import slugify from 'slugify';
import { useShoppingCartStore } from '@/Stores/ShoppingCart/ShoppingCartStore';
import { storeToRefs } from 'pinia';
import ApplicationSettingModel from '@/Interfaces/ApplicationSettings/ApplicationSettingModel';

const props = defineProps<{
    categoryName: string,
    category: CategoryModel,
    appSettings: ApplicationSettingModel
}>();

const { makeWishList, getShopingCartItems } = useShoppingCartComposable();

const shoppingStore = useShoppingCartStore();
const { localStorageCartItems, localStorageWishListItems } = storeToRefs(shoppingStore);

const categoryProduct = ref<ProductModel[]|null>(null);

const showBanner = ref<boolean>(true);
const products = ref<EcommerceProductModel|null>(null);

const noImage = ref<string|null>('/No-Image-Placeholder.svg');

const responsiveOptions = ref([
    {
        breakpoint: '1400px',
        numVisible: 2,
        numScroll: 1
    },
    {
        breakpoint: '1199px',
        numVisible: 3,
        numScroll: 1
    },
    {
        breakpoint: '767px',
        numVisible: 2,
        numScroll: 1
    },
    {
        breakpoint: '575px',
        numVisible: 1,
        numScroll: 1
    }
]);

const isMobile = ref(false);
const onResize = () => (isMobile.value = window.innerWidth <= 575);

const getSeverity = (status: number) => {
    switch (status) {
        case 0:
            return 'success';

        case 1:
            return 'danger';

        default:
            return null;
    }
};




const categoryBanner = computed(() => isValidJSON(props.category?.banner?.banner_image) ? JSON.parse(props.category?.banner?.banner_image)[0] : null);

onMounted(() => {
    if (props.category?.category_products.length > 0) {
        categoryProduct.value = props.category?.category_products;
    } else if (props.category?.sub_category_products.length > 0) {
        categoryProduct.value = props.category?.sub_category_products;
    } else if (props.category?.sub_sub_category_products.length > 0) {
        categoryProduct.value = props.category?.sub_sub_category_products;
    }

    onResize();
    window.addEventListener('resize', onResize);
    // Let PrimeVue re-measure after mount
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
});

onUnmounted(() => window.removeEventListener('resize', onResize));
</script>

<style scoped>
/* Lock item basis so slides never vanish after first scroll */
.p-carousel .p-carousel-item { flex: 0 0 auto; }

/* Guarantee 2-up on mobile even if internal calc glitches */
@media (max-width: 767px) {
  .p-carousel .p-carousel-item { flex-basis: 50% !important; }
}
</style>